// v2.9.10 — REGISTRAR DESENLACE (canal-agnóstico), como FALLBACK discreto ("Registrar por otra vía").
// SIN motivo_desvio: la monitorista NO justifica por qué usó otra vía; no se infiere "intención humana".
// Se conserva SIEMPRE: metodo estructurado + actor + timestamp + desenlace. Si YA existe un desenlace
// (p.ej. derivado al registrar el resultado), se muestra sólo un RESUMEN read-only.
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";

const METODOS = [
  { v: "audio", label: "Audio con la persona" },
  { v: "telefono_persona", label: "Teléfono de la persona" },
  { v: "contacto_autorizado", label: "Contacto autorizado" },
  { v: "personal_en_sitio", label: "Personal / cuidadora en sitio" },
  { v: "verificacion_presencial", label: "Verificación presencial" },
  { v: "otro", label: "Otro método" },
];
// INSTITUCIONAL: MISMOS valores del contrato (no se inventan códigos ni se amplía el enum); sólo
// cambia la proyección: quién del CENTRO verificó. Teléfono de la persona y contacto autorizado
// dejan de ofrecerse como vía primaria (la familia es escalamiento secundario).
const METODOS_INST = [
  { v: "personal_en_sitio", label: "Atendido por personal del centro en la habitación" },
  { v: "verificacion_presencial", label: "Verificación presencial del responsable de turno / enfermería / supervisión" },
  { v: "audio", label: "Comunicación con la habitación (apoyo · no sustituye la revisión del personal)" },
  { v: "otro", label: "Otro medio interno del centro" },
];

export default function DesenlaceDirectoControl({ detail, onRegistrarDesenlace, busy, casoId = null }) {
  const [cid, setCid] = useState("");
  const [metodo, setMetodo] = useState("");
  // CORRECCIÓN F — el control pertenece a UN caso. Antes de limpiar método o contacto se comprueba
  // la identidad ACTUAL: que este control siga montado y que la respuesta sea de SU caso. La
  // respuesta tardía del caso A nunca borra las selecciones del caso B.
  const propio = casoId || detail?.id || null;
  const vivo = useRef(true);
  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);
  const aplicaAEsteControl = (r) => vivo.current && (!r?.casoId || !propio || r.casoId === propio);
  if (detail?.es_tecnica) return null;
  if (["recibida", "cerrada"].includes(detail?.estado)) return null;

  const instMode = (detail?.modalidad_respuesta || detail?._modalidad) === "institucional";
  const metodos = instMode ? METODOS_INST : METODOS;

  // Si YA existe un desenlace (auto-derivado o previo), NO invitar a re-registrarlo: sólo RESUMEN.
  const desHecho = detail?.verificacion_desenlace?.codigo;
  if (desHecho) {
    const vd = detail.verificacion_desenlace;
    const metodoLabel = (metodos.find((m) => m.v === vd.metodo) || METODOS.find((m) => m.v === vd.metodo) || {}).label
      || (vd.codigo === "VERIFICADO_CONTACTO_AUTORIZADO" ? "Contacto autorizado" : null);
    const titulo = vd.codigo === "VERIFICADO_CONTACTO_AUTORIZADO"
      ? (instMode ? "Confirmado por un contacto autorizado (aviso secundario)" : "Verificado por contacto autorizado")
      : vd.codigo === "NO_VERIFICABLE" ? "No verificable"
        : instMode ? "Atención verificada por el personal del centro" : "Verificado directamente";
    return (
      <div data-testid="v3-desenlace-resumen" className="mt-3 rounded-xl border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/15 p-4">
        <p className="flex items-center gap-1.5 text-sm font-bold text-emerald-700 dark:text-emerald-300">
          <ShieldCheck className="h-4 w-4" /> {titulo}
        </p>
        {metodoLabel && <p className="mt-1 text-xs text-slate-600 dark:text-slate-400" data-testid="v3-desenlace-resumen-metodo">{metodoLabel}</p>}
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Registra la nota factual final al cerrar el caso.</p>
      </div>
    );
  }

  const snap = (detail?.action_plan_congelado?.contactos_snapshot) || [];
  const confirman = snap.filter((c) => c.puede_confirmar_estado && (c.telefono_valido || c.telefono_e164));

  // v2.9.10 — Sólo se pide la VÍA usada. Sin motivo de desvío.
  const puedeVerificar = !!metodo;

  return (
    <div data-testid="v3-desenlace-directo" className="mt-3 rounded-xl border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/15 p-4">
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
        <ShieldCheck className="h-4 w-4" /> {instMode ? "Registrar atención por otra vía" : "Registrar por otra vía"}
      </p>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
        {instMode
          ? "Cuando el personal del centro haya verificado al residente, indica quién lo verificó. La nota factual se pide al cerrar."
          : "Cuando confirmes el estado real de la persona, indica por qué vía lo verificaste. La nota factual se pide al cerrar."}
      </p>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{instMode ? "Quién verificó · rol interno del centro" : "Vía de verificación"}</label>
          <select data-testid="v3-desenlace-metodo-select" value={metodo} onChange={(e) => setMetodo(e.target.value)}
            className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200">
            <option value="">{instMode ? "Selecciona quién verificó…" : "Selecciona la vía…"}</option>
            {metodos.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button data-testid="v3-desenlace-directo-verificado" disabled={busy || !puedeVerificar}
          onClick={async () => {
            // REPARACIÓN M6-D — se espera el resultado antes de limpiar: un rechazo o una respuesta
            // incierta conservan la selección mientras el caso siga siendo accesible.
            const r = await onRegistrarDesenlace({ codigo: "VERIFICADO_DIRECTO", metodo, motivo_desvio: null });
            if (!aplicaAEsteControl(r)) return;
            if (!r || !r.estado || String(r.estado).startsWith("confirmado") || r.autorizacion) setMetodo("");
          }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
          {instMode ? "Atención verificada por el personal" : "La persona está verificada"}
        </Button>
        {!instMode && confirman.length > 0 && (
          <div className="flex items-center gap-2">
            <select data-testid="v3-desenlace-directo-contacto-select" value={cid} onChange={(e) => setCid(e.target.value)}
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200">
              <option value="">Verificado por contacto autorizado…</option>
              {confirman.map((c) => <option key={c.contacto_id} value={c.contacto_id}>{c.nombre || "Contacto"}</option>)}
            </select>
            <Button data-testid="v3-desenlace-directo-contacto-btn" disabled={busy || !cid}
              onClick={async () => {
                const r = await onRegistrarDesenlace({ codigo: "VERIFICADO_CONTACTO_AUTORIZADO", contacto_id: cid });
                if (!aplicaAEsteControl(r)) return;
                if (!r || !r.estado || String(r.estado).startsWith("confirmado") || r.autorizacion) setCid("");
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              Confirmar por contacto
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
